// Thin wrappers around window.Telegram.WebApp + @telegram-apps/sdk-react.
//
// The raw-WebApp wrappers stay for code that only needs to read identity
// (initData, theme, platform). The SDK init + back-button hook below are
// what RCF-027 adds — they let React components hook Telegram's hardware
// back button into their own state machines, which is the key UX win
// when running an in-Telegram multi-step wizard.

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  init as sdkInit,
  backButton,
  mountBackButton,
} from "@telegram-apps/sdk-react";

function tg() {
  return typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;
}

// True when the SPA is running inside a Telegram Mini App context, i.e.
// initData has actually been injected by the Telegram client. We can't rely
// on @telegram-apps/sdk's isTMA() here: telegram-web-app.js is loaded in
// index.html so `window.Telegram.WebApp` exists everywhere, and the SDK
// happily reports "TMA mode" inside a regular browser. Checking initData
// directly is the only signal that distinguishes "opened from Telegram"
// from "user typed the URL into Chrome".
export function isInTelegram() {
  return Boolean(tg()?.initData);
}

let sdkReady = false;
let backButtonMounted = false;

export function initTelegram() {
  const wa = tg();
  if (wa) {
    wa.ready();
    wa.expand();
    const bg = wa.themeParams?.bg_color;
    if (bg) document.documentElement.style.setProperty("--tg-bg-color", bg);
  }
  // Light-touch SDK init — safe to call when not running inside Telegram
  // (isInTelegram() returns false → we skip). v2 SDK uses signals, no Provider.
  // Note: using our stricter initData-based check rather than the SDK's
  // isTMA(), which is too permissive when telegram-web-app.js is loaded.
  if (!sdkReady && isInTelegram()) {
    try { sdkInit({ acceptCustomStyles: true }); sdkReady = true; }
    catch (err) { console.warn("Telegram SDK init failed:", err); }
  }
}

export function getThemeAppearance() {
  return tg()?.colorScheme === "dark" ? "dark" : "light";
}

export function getPlatform() {
  return tg()?.platform || "web";
}

export function getInitData() {
  return tg()?.initData || "";
}

export function getInitDataUnsafe() {
  return tg()?.initDataUnsafe || {};
}

export function close() {
  tg()?.close();
}

export function openLink(url) {
  const wa = tg();
  if (wa?.openLink) wa.openLink(url);
  else window.open(url, "_blank");
}

// Bind Telegram's hardware back button to a handler for the lifetime of
// a component. Pass `null` (or a falsy value) to hide the back button —
// useful at the top of a wizard. No-ops gracefully outside Telegram.
export function useTelegramBack(handler) {
  useEffect(() => {
    if (!handler || !isInTelegram()) return;
    try {
      if (!backButtonMounted) { mountBackButton(); backButtonMounted = true; }
      backButton.show();
    } catch {
      return; // older Telegram clients lack the back-button entirely
    }
    const off = backButton.onClick(handler);
    return () => {
      try { backButton.hide(); } catch {}
      // onClick returns an unsubscriber in sdk-react v2.
      if (typeof off === "function") off();
    };
  }, [handler]);
}

// Default page-level back wiring: show Telegram's back button on every
// route except the home root. Tapping it navigates to `parent` (logical
// parent route) — using navigate() instead of history.back() so deep
// links (opened directly into a sub-page from a notification) still go
// somewhere sensible instead of bouncing the user out of the Mini App.
// Outside Telegram this is a no-op.
export function usePageBack(parent = "/") {
  const navigate = useNavigate();
  useTelegramBack(() => navigate(parent));
}
